"use client";

import { useMemo, useState } from "react";
import Image from "next/image";
import { Check, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { GLOBAL_ACTIONABLE_BOOK_IDS } from "@/lib/sportsbook-preferences";
import { groupBooks, hasNamedGroups } from "@/lib/book-catalog";
import { useBookCatalog } from "@/contexts/book-catalog-context";
import { useBookAvailability } from "@/contexts/book-availability-context";
import { keepHidden } from "@/lib/book-location";
import { LocationBar } from "@/components/odds-screen/live/book-panel";
import { useIsMobile } from "@/hooks/use-mobile";
import { getSportsbookLogoFilename } from "@/lib/sportsbook-logo";
import { sportsbookLogoUrl } from "@/lib/sportsbook-logo-url";
import { sportsbookIdToDisplay, type SportsbookId } from "@/lib/sportsbook-registry";

interface SportsbookSelectionFieldProps {
  selectedBookIds: readonly SportsbookId[];
  onChange: (bookIds: SportsbookId[]) => void;
  availableBookIds?: readonly SportsbookId[];
  disabled?: boolean;
  descriptionId?: string;
}

export function SportsbookSelectionField({
  selectedBookIds,
  onChange,
  availableBookIds = GLOBAL_ACTIONABLE_BOOK_IDS,
  disabled = false,
  descriptionId,
}: SportsbookSelectionFieldProps) {
  const catalog = useBookCatalog();
  const { isHere, place } = useBookAvailability();
  const isMobile = useIsMobile() === true;
  const [search, setSearch] = useState("");
  const selectedSet = useMemo(
    () => new Set(selectedBookIds),
    [selectedBookIds],
  );
  const normalizedSearch = search.trim().toLowerCase();

  const groups = useMemo(() => groupBooks(catalog, availableBookIds), [catalog, availableBookIds]);
  const named = hasNamedGroups(groups);
  const groupedBooks = useMemo(() => {
    return groups.map((group) => ({
      ...group,
      books: group.books.filter((id) => {
        if (!normalizedSearch) return true;
        return (
          sportsbookIdToDisplay(id).toLowerCase().includes(normalizedSearch) ||
          id.includes(normalizedSearch)
        );
      }).sort((left, right) =>
        sportsbookIdToDisplay(left).localeCompare(sportsbookIdToDisplay(right), undefined, {
          sensitivity: "base",
        }),
      ),
    })).filter((group) => group.books.length > 0);
  }, [groups, normalizedSearch]);

  const emitSelection = (next: Iterable<SportsbookId>) => {
    const kept = new Set(keepHidden([...next], selectedBookIds, isHere));
    onChange(availableBookIds.filter((id) => kept.has(id)));
  };

  const toggleBook = (bookId: SportsbookId) => {
    const next = new Set(selectedSet);
    if (next.has(bookId)) next.delete(bookId);
    else next.add(bookId);
    emitSelection(next);
  };

  const bookButton = (book: SportsbookId) => {
    const selected = selectedSet.has(book);
    const away = !isHere(book);
    return (
      <button
        key={book}
        type="button"
        role="checkbox"
        aria-checked={selected}
        aria-disabled={away || undefined}
        disabled={disabled}
        onClick={away ? undefined : () => toggleBook(book)}
        className={cn(
          "relative flex min-h-12 items-center gap-2 rounded-md border px-2.5 py-2 text-left transition-colors sm:min-h-14",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:opacity-50",
          away
            ? "cursor-not-allowed border-white/[0.07] bg-white/[0.02] text-muted-foreground opacity-60"
            : selected
            ? "border-primary/50 bg-primary/[0.09] text-foreground"
            : "border-white/[0.07] bg-white/[0.02] text-muted-foreground hover:border-white/15 hover:bg-white/[0.05] hover:text-foreground",
        )}
      >
        <span className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded bg-white/90">
          <Image
            src={sportsbookLogoUrl(getSportsbookLogoFilename(book))}
            alt=""
            width={26}
            height={26}
            className="object-contain"
          />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-xs font-medium">
            {sportsbookIdToDisplay(book)}
          </span>
        </span>
        <span
          aria-hidden="true"
          className={cn(
            "flex size-4 shrink-0 items-center justify-center rounded-full border",
            selected
              ? "border-primary bg-primary text-primary-foreground"
              : "border-white/20",
          )}
        >
          {selected ? <Check className="size-3" /> : null}
        </span>
      </button>
    );
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2.5 sm:gap-3">
      <LocationBar sheet={isMobile} className="rounded-md border border-surface-line px-1.5 py-1" />
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative min-w-0 flex-1">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search sportsbooks…"
            aria-label="Search Books"
            className="min-h-11 pl-9"
            disabled={disabled}
          />
        </div>
        <div className="flex items-center justify-between gap-2 sm:justify-end">
          <span className="text-xs text-muted-foreground">
            {selectedBookIds.length} selected
          </span>
          <button
            type="button"
            className="min-h-11 rounded-md px-3 text-xs font-medium text-primary transition-colors hover:bg-primary/10 disabled:opacity-50"
            onClick={() => emitSelection(availableBookIds)}
            disabled={disabled}
          >
            Select all
          </button>
          <button
            type="button"
            className="min-h-11 rounded-md px-3 text-xs font-medium text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground disabled:opacity-50"
            onClick={() => emitSelection([])}
            disabled={disabled || !selectedBookIds.some(isHere)}
          >
            Clear
          </button>
        </div>
      </div>

      <div
        className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain pr-1 scrollbar-custom sm:space-y-5"
        aria-describedby={descriptionId}
      >
        {groupedBooks.map(({ regulation, label, books }) => (
          <section key={regulation ?? "other"} aria-labelledby={named ? `sportsbook-category-${regulation ?? "other"}` : undefined}>
            {named && (
              <h3
                id={`sportsbook-category-${regulation ?? "other"}`}
                className="mb-2 data-num text-[11px] uppercase tracking-[0.16em] text-muted-foreground"
              >
                {label}
              </h3>
            )}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">{books.filter(isHere).map(bookButton)}</div>
            {books.some((book) => !isHere(book)) && (
              <>
                <p className="mt-3 text-[10px] font-medium uppercase tracking-[0.06em] text-zinc-500">Not available in {place.state}</p>
                <p className="mb-2 text-[11px] text-zinc-500">Turn off Available here only to use these.</p>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">{books.filter((book) => !isHere(book)).map(bookButton)}</div>
              </>
            )}
          </section>
        ))}

        {groupedBooks.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">
            No Books match “{search.trim()}”.
          </p>
        ) : null}
      </div>
    </div>
  );
}
